/* assets/luba-directions.js — shared "Directions" chooser
 *
 * Both the locator and the events page linked straight to Google Maps. Plenty
 * of people have no Google Maps app (or account), so the link dead-ends for
 * them. This turns the button into a small chooser: Apple Maps, Google Maps,
 * or copy the address.
 *
 * Deliberately NOT platform-sniffed into a default. An iPhone user who prefers
 * Google Maps is common, and guessing wrong is exactly the failure we are
 * fixing. The choice is offered; the browser handles the handoff.
 *
 * Shared asset rather than a copy in each stylesheet/script, because both pages
 * need identical behaviour and the two section scripts are otherwise
 * independent files with no module system between them.
 */
(function () {
  'use strict';

  if (window.LubaDirections) return;

  var MENU = null;          // one menu element, reused
  var OPEN_FOR = null;      // the trigger it currently belongs to

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function appleUrl(addr) {
    // daddr = destination address. On iOS/macOS this opens Maps directly; on
    // other platforms it lands on Apple's web map, which is a fair fallback.
    return 'https://maps.apple.com/?daddr=' + encodeURIComponent(addr) + '&dirflg=d';
  }

  function googleUrl(addr) {
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(addr);
  }

  function copyAddress(addr, done) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(addr).then(function () { done(true); }, function () { done(false); });
      return;
    }
    // Older Safari / non-secure contexts.
    try {
      var ta = document.createElement('textarea');
      ta.value = addr;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:-9999px;';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      done(ok);
    } catch (e) { done(false); }
  }

  function close() {
    if (!MENU) return;
    MENU.hidden = true;
    if (OPEN_FOR) {
      OPEN_FOR.setAttribute('aria-expanded', 'false');
      OPEN_FOR = null;
    }
  }

  function ensureMenu() {
    if (MENU) return MENU;
    MENU = el('div', 'lbdir-menu');
    MENU.setAttribute('role', 'menu');
    MENU.hidden = true;
    // ⚠ Appended to BODY, not to the row. On desktop the locator's results list
    // is a capped `overflow-y: auto` container, which would clip a menu
    // rendered inside a row. position:fixed alone is not enough either — any
    // ancestor with a transform would still trap it. Body level always works.
    document.body.appendChild(MENU);

    document.addEventListener('click', function (ev) {
      if (!MENU.hidden && !MENU.contains(ev.target) && ev.target !== OPEN_FOR &&
          (!OPEN_FOR || !OPEN_FOR.contains(ev.target))) close();
    }, true);
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && !MENU.hidden) {
        var t = OPEN_FOR;
        close();
        if (t) t.focus();
      }
    });
    window.addEventListener('resize', close);
    // Capture phase so it also catches scrolling inside the results list.
    window.addEventListener('scroll', close, true);
    return MENU;
  }

  function place(menu, trigger) {
    var r = trigger.getBoundingClientRect();
    menu.hidden = false;                     // measure after it is laid out
    var mh = menu.offsetHeight, mw = menu.offsetWidth;
    var gap = 6;
    var top = r.bottom + gap;
    if (top + mh > window.innerHeight - 8) {
      var above = r.top - mh - gap;
      top = above > 8 ? above : Math.max(8, window.innerHeight - mh - 8);
    }
    // Clamp to the viewport regardless of where the trigger sits. Flipping is
    // relative to the button, so a trigger that is itself off-screen (page
    // scrolled between the rect read and here, or a partially visible row)
    // would otherwise place a fixed menu somewhere nobody can reach it.
    var maxTop = window.innerHeight - mh - 8;
    top = maxTop < 8 ? 8 : Math.min(Math.max(8, top), maxTop);

    var left = r.right - mw;                 // right-aligned to the button
    var maxLeft = window.innerWidth - mw - 8;
    left = maxLeft < 8 ? 8 : Math.min(Math.max(8, left), maxLeft);
    menu.style.top = Math.round(top) + 'px';
    menu.style.left = Math.round(left) + 'px';
  }

  function open(trigger, addr) {
    var menu = ensureMenu();
    menu.innerHTML = '';

    var head = el('div', 'lbdir-head', addr);
    menu.appendChild(head);

    [['Apple Maps', appleUrl(addr)], ['Google Maps', googleUrl(addr)]].forEach(function (pair) {
      var a = el('a', 'lbdir-item', pair[0]);
      a.href = pair[1];
      a.target = '_blank';
      a.rel = 'noopener';
      a.setAttribute('role', 'menuitem');
      a.addEventListener('click', close);
      menu.appendChild(a);
    });

    var copy = el('button', 'lbdir-item lbdir-copy', 'Copy address');
    copy.type = 'button';
    copy.setAttribute('role', 'menuitem');
    copy.addEventListener('click', function () {
      copyAddress(addr, function (ok) {
        if (ok) {
          copy.textContent = 'Copied';
          copy.classList.add('is-done');
          setTimeout(close, 700);
          return;
        }
        // Clipboard access can be refused (no user activation, permission
        // denied, insecure context). Select the address shown at the top of the
        // menu so "press Ctrl+C" is actually a true instruction — telling
        // someone to copy with nothing selected is worse than saying nothing.
        try {
          var sel = window.getSelection();
          sel.removeAllRanges();
          var range = document.createRange();
          range.selectNodeContents(head);
          sel.addRange(range);
          copy.textContent = 'Address selected - press Ctrl+C';
        } catch (e) {
          copy.textContent = 'Copy unavailable';
        }
        copy.classList.add('is-done');
        setTimeout(close, 3000);
      });
    });
    menu.appendChild(copy);

    OPEN_FOR = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    place(menu, trigger);
    var first = menu.querySelector('.lbdir-item');
    if (first) first.focus();
  }

  window.LubaDirections = {
    /**
     * Turn `trigger` into a directions chooser for `address`.
     * Works on a <button> or an <a>; an <a> keeps its href as a no-JS fallback.
     */
    attach: function (trigger, address) {
      if (!trigger || !address) return;
      trigger.setAttribute('aria-haspopup', 'menu');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', function (ev) {
        ev.preventDefault();
        // The locator's rows are themselves clickable; without this, choosing
        // directions would also select the row behind the button.
        ev.stopPropagation();
        if (OPEN_FOR === trigger && MENU && !MENU.hidden) { close(); return; }
        open(trigger, address);
      });
    },
    close: close
  };
})();
